<script setup>
import { ref, computed, onMounted } from 'vue';
import Select2Component from "../../Select2Component.vue";

defineProps({
  error: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['save', 'cancel']);

const fieldId = 'find_address_v2';
const newAddress = ref('');
const newCity = ref('');
const newZip = ref('');
const addressOptions = ref([]);
let SuggestionClass = null;
let SessionTokenClass = null;
let PlaceClass = null;
let sessionToken = null;

const canSave = computed(() => !!(newAddress.value || newCity.value || newZip.value));

onMounted(async () => {
  const {
    AutocompleteSuggestion,
    AutocompleteSessionToken,
    Place,
  } = await google.maps.importLibrary('places');
  SuggestionClass = AutocompleteSuggestion;
  SessionTokenClass = AutocompleteSessionToken;
  PlaceClass = Place;
  sessionToken = new SessionTokenClass();
});

function save() {
  emit('save', {
    address: newAddress.value,
    city: newCity.value,
    zip: newZip.value,
  });
}

function cancel() {
  emit('cancel');
}

async function onAddressFilter(val) {
  let searchString = val.trim();
  if (searchString === newAddress.value && addressOptions.value.length > 0 || searchString.length < 3) {
    return;
  }

  try {
    const request = {
      input: searchString,
      sessionToken,
      includedRegionCodes: ['NO'],
      includedPrimaryTypes: ['street_address', 'premise'],
    };
    const { suggestions } = await SuggestionClass.fetchAutocompleteSuggestions(request);

    addressOptions.value = suggestions.map(s => {
      const p = s.placePrediction;

      return {
        label: p.text.text,
        value: p.placeId,
        street: p.mainText.text,
      };
    });
  } catch (e) {
    console.error("AutocompleteSuggestion Error:", e);
  }
}

async function onAddressSelect(selected) {
  if (!selected) {
    newCity.value = null;
    newZip.value = null;
    newAddress.value = null;
    return;
  }

  try {
    const place = new PlaceClass({
      id: selected,
      requestedLanguage: 'no',
    });
    await place.fetchFields({
      fields: ['addressComponents'],
    });
    let street = '';
    let number = '';
    if (place.addressComponents) {
      for (const component of place.addressComponents) {
        if (component.types.includes('locality') || component.types.includes('postal_town')) {
          newCity.value = component.longText;
        }
        if (component.types.includes('postal_code')) {
          newZip.value = component.longText;
        }
        if (component.types.includes('route')) {
          street = component.longText;
        }
        if (component.types.includes('street_number')) {
          number = component.longText;
        }
      }
      newAddress.value = `${street} ${number}`;
    }
    sessionToken = new SessionTokenClass();
  } catch (e) {
    console.error("Place fetchFields Error:", e);
  }
}
</script>

<template>
  <div>
    <label class="form-label" :class="{ 'text-danger': !!error }" :for="fieldId">
      {{ trans('order_deliveries.modal.edit_address_title') }}
    </label>
    <div class="d-flex flex-wrap align-items-center v2-address-row mb-2">
      <div class="v2-address-input" :class="{ 'border border-danger rounded': !!error }">
        <select-2-component
          :id="fieldId"
          :prop_options="addressOptions"
          prop_name="label"
          :prop_required="true"
          :prop_filterable="false"
          :prop_placeholder="trans('order_deliveries.modal.change_address.find_address')"
          @search="onAddressFilter($event)"
          @changes="onAddressSelect($event)"
        />
      </div>
      <button
        type="button"
        class="btn dark-button extra-rounded v2-address-btn"
        :disabled="!canSave"
        @click="save"
      >
        {{ trans('order_deliveries.modal.save_and_recalculate') }}
      </button>
      <button
        type="button"
        class="btn extra-rounded v2-address-btn v2-btn-outline"
        @click="cancel"
      >
        {{ trans('order_deliveries.modal.cancel_edit_address') }}
      </button>
    </div>
    <div v-if="!!error" class="text-danger mb-2">
      <i class="fas fa-circle-exclamation" /> {{ error }}
    </div>
  </div>
</template>

<style scoped>
.v2-address-row {
  gap: 10px;
  min-height: 46px;
}
.v2-address-input {
  flex: 1 1 260px;
  min-width: 200px;
}

.v2-address-input :deep(.vs--searchable .vs__dropdown-toggle) {
  padding: 0.5rem !important;
}

.v2-address-input :deep(.vs__selected-options) {
  flex-wrap: nowrap;
  min-width: 0;
}
.v2-address-input :deep(.vs__actions) {
  flex-shrink: 0;
}
.v2-address-input :deep(.vs__selected) {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.v2-address-btn {
  white-space: nowrap;
  font-size: 0.9rem;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-top: 0;
  padding-bottom: 0;
  min-height: 46px;
}
.dark-button:hover {
  color: #f5f5f5;
  background-color: #0b5ed7;
  border-color: #0a58ca;
}
.v2-btn-outline {
  color: #111886;
  border: 1px solid #111886;
  background: #fff;
}
.v2-btn-outline:hover {
  background: #f1f5fd;
}
.dark-button:disabled,
.dark-button.disabled {
  background: #111886;
  border-color: #111886;
}
</style>
